import React, { useState } from "react";
import { Box, SpaceBetween, Modal, Button } from "@amzn/awsui-components-react";
import { IPropsProjectDetailsEditableModal } from "../../../../types/SchemaTypes";
import { IFieldSchema } from "external/programs/migration-acceleration-program/2024/fund-request/types/SchemaTypes";
import { FieldType } from "shared/programs/migration-acceleration-program/2024/fund-request/components/fields/FieldComponent";
import { ValidationType } from "shared/programs/migration-acceleration-program/2024/fund-request/components/fields/validations";
import InputSectionFieldsFactory from "shared/programs/migration-acceleration-program/2024/fund-request/components/fields/input/InputSectionFieldsFactory";
import { IGenericObject } from "../../../../types/CommonTypes";
import { IMAPFundRequest } from "shared/programs/migration-acceleration-program/2024/fund-request/types/SchemaTypes";
import {
handleApiError,
Result,
ResultType,
Success,
} from "shared/util/api/util";
import { hasError, retryApiCall } from "shared/util/services/data/DataService";
import { updateProjectInformation } from "external/util/services/data/FundRequestService";

const ProjectDetailsEditableModal = ({
dataProps,
title,
setModalOpen,
editableStages,
}: IPropsProjectDetailsEditableModal) => {
const schema: IFieldSchema[] = [
    {
    label: "AWS account ID",
    keyName: "awsAccountId",
    placeholder: "AWS account ID",
    fieldType: FieldType.Input,
    editableStages: editableStages,
    validations: [ValidationType.AwsAccountId],
    },
];

const [fieldErrors, setFieldErrors] = useState<IGenericObject>({});
const [data, setData] = useState<IProjectDetails>(dataProps);
const [isLoading, setIsLoading] = useState<boolean>(false);
const [formError, setFormError] = useState<string>("");

const onChange = ({
    key,
    value,
}: {
    key: string;
    value: string | boolean | undefined;
}) => {
    if (formError) setFormError("");
    if (Object.keys(fieldErrors).length > 0) setFieldErrors({});

    setData((prevData) => ({ ...prevData, [key]: value }));
};

const onError = ({ key, value }: { key: string; value: string }) => {
    setFieldErrors((prevErrors) => ({ ...prevErrors, [key]: value }));
};

const editProjectDetails = async (
    data: IMAPFundRequest
): Promise<Result<ResultType>> => {
    setIsLoading(true);
    const payload: IGenericObject = {
    awsAccountId: data["awsAccountId"],
    activityName: data["activityName"],
    description: data["description"],
    executedCountry: data["executedCountry"],
    executedState: data["executedState"].split(","),
    plannedStartDate: {
        timestamp: new Date(data["plannedStartDate"]).getTime(),
    },
    plannedEndDate: { timestamp: new Date(data["plannedEndDate"]).getTime() },
    };

    const response = await retryApiCall({
    callApi: updateProjectInformation(payload),
    error: {},
    });

    setIsLoading(false);

    if (hasError(response)) {
    setFieldErrors(response.errors);
    return handleApiError(response.errorType)(response);
    }
    updateStateOfDataProps();
    closeModal();
    return new Result<Success>({});
};

const updateStateOfDataProps = (): void => {
    dataProps.awsAccountId = data.awsAccountId;
};

const closeModal = (): void => {
    setModalOpen(false);
};

return (
    <Modal
    id="project-details-editable-modal"
    onDismiss={() => closeModal()}
    visible={true}
    closeAriaLabel="Close modal"
    size="large"
    header={title}
    footer={
        <Box float="right">
        <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={() => closeModal()}>Cancel</Button>
            <Button
            variant="primary"
            disabled={false}
            loading={isLoading}
            onClick={async () => {
                editProjectDetails(data);
            }}
            >
            Submit
            </Button>
        </SpaceBetween>
        </Box>
    }
    >
    <SpaceBetween size="l">
        <InputSectionFieldsFactory
        data={data}
        errors={fieldErrors}
        onChange={onChange}
        onError={onError}
        schema={schema}
        />
    </SpaceBetween>
    </Modal>
);
};

export default ProjectDetailsEditableModal;

export interface IProjectDetails extends IMAPFundRequest {}